// Pure, framework-free eligibility engine for the "Adjusting a lot?" Auto Schedule nudge.
// Every branch is unit-tested against an injected `now`. No Date.now() here — callers pass the clock.

export interface AutoScheduleNudgeState {
  /** Epoch-ms of committed manual temperature changes, pruned to the trigger window. */
  adjustments: number[];
  /** How many impressions have ever been started (capped by NudgeConfig.lifetimeCap). */
  lifetimeShownCount: number;
  /** When the currently-visible impression first appeared; null when none is active. */
  activeImpressionStartedAt: number | null;
  /** Epoch-ms until which the nudge is suppressed after a dismissal; null when not in cooldown. */
  suppressedUntil: number | null;
  /** Permanent kill switch: once Auto Schedule is adopted, the nudge never returns (v1). */
  retiredByAdoption: boolean;
}

export type NudgeEventType = 'shown' | 'dismissed' | 'expired' | 'setup_completed';

/**
 * An emitted nudge lifecycle event, tagged with the id of the impression it
 * belongs to (the epoch-ms the impression started). The id lets downstream
 * analytics group every event of a single impression and tie a `shown` to its
 * eventual dismiss/expiry/completion. A single `evaluate()` can emit events for
 * two different impressions (an old one expiring and a new one starting), so the
 * id lives per-event, not per-result.
 */
export interface NudgeEvent {
  type: NudgeEventType;
  impressionId: number;
}

export interface NudgeConfig {
  triggerCount: number;
  triggerWindowMs: number;
  dismissCooldownMs: number;
  expirationMs: number;
  lifetimeCap: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_NUDGE_CONFIG: NudgeConfig = {
  triggerCount: 3,
  triggerWindowMs: 7 * DAY_MS,
  dismissCooldownMs: 14 * DAY_MS,
  expirationMs: 5 * DAY_MS,
  lifetimeCap: 3,
};

export type NudgeArm = 'treatment' | 'control';

/**
 * Percentage of eligible households withheld as a non-nudged **control** arm so
 * that lift (nudged vs. control Auto Schedule setup rate — the PRD's primary
 * success metric) can be measured. Hardcoded for now; a future follow-up could
 * drive it from remote config to ramp the experiment down after launch.
 *
 * TEMPORARILY DISABLED: set to 0 so every eligible household is `treatment` and
 * the banner always shows. Restore to 50 to re-enable the control holdout.
 */
export const HOLDOUT_PERCENT = 0;

/**
 * Deterministic, stable experiment-arm assignment from the household id. The
 * same `unitId` always resolves to the same arm — on every device and after a
 * reinstall (unlike the per-device nudge state) — so a household is never split
 * across arms. An unknown id returns `treatment`: we never withhold the nudge
 * from a household we can't identify.
 */
export function assignArm(
  unitId: string | undefined,
  holdoutPercent: number = HOLDOUT_PERCENT,
): NudgeArm {
  if (!unitId) return 'treatment';
  let hash = 5381;
  for (let i = 0; i < unitId.length; i++) {
    hash = ((hash << 5) + hash + unitId.charCodeAt(i)) >>> 0; // djb2, kept unsigned 32-bit
  }
  return hash % 100 < holdoutPercent ? 'control' : 'treatment';
}

export interface ReduceResult {
  state: AutoScheduleNudgeState;
  visible: boolean;
  events: NudgeEvent[];
}

export function initialNudgeState(): AutoScheduleNudgeState {
  return {
    adjustments: [],
    lifetimeShownCount: 0,
    activeImpressionStartedAt: null,
    suppressedUntil: null,
    retiredByAdoption: false,
  };
}

/** Append a committed manual adjustment and prune entries outside the rolling window. */
export function recordAdjustment(
  state: AutoScheduleNudgeState,
  now: number,
  config: NudgeConfig = DEFAULT_NUDGE_CONFIG,
): AutoScheduleNudgeState {
  const cutoff = now - config.triggerWindowMs;
  const adjustments = [...state.adjustments, now].filter((t) => t > cutoff);
  return { ...state, adjustments };
}

/** The core eligibility decision. Pure: identical inputs produce identical outputs. */
export function evaluate(
  state: AutoScheduleNudgeState,
  now: number,
  hasAnyAutoSet: boolean,
  config: NudgeConfig = DEFAULT_NUDGE_CONFIG,
): ReduceResult {
  // 1. Adoption (now or previously) permanently retires the nudge (v1: no resume).
  if (hasAnyAutoSet || state.retiredByAdoption) {
    const firstRetire = !state.retiredByAdoption;
    const events: NudgeEvent[] =
      firstRetire && state.activeImpressionStartedAt != null
        ? [{ type: 'setup_completed', impressionId: state.activeImpressionStartedAt }]
        : [];
    return {
      state: { ...state, retiredByAdoption: true, activeImpressionStartedAt: null },
      visible: false,
      events,
    };
  }

  // 2. Dismissal cooldown.
  if (state.suppressedUntil != null && now < state.suppressedUntil) {
    return { state, visible: false, events: [] };
  }

  // 3. An impression is currently active.
  if (state.activeImpressionStartedAt != null) {
    const expired = now - state.activeImpressionStartedAt >= config.expirationMs;
    if (!expired) {
      return { state, visible: true, events: [] };
    }
    // Passive expiry: stop showing, no cooldown, then re-check the trigger.
    const expiredImpressionId = state.activeImpressionStartedAt;
    const cleared: AutoScheduleNudgeState = { ...state, activeImpressionStartedAt: null };
    const next = tryTrigger(cleared, now, config);
    return {
      state: next.state,
      visible: next.visible,
      events: [{ type: 'expired', impressionId: expiredImpressionId }, ...next.events],
    };
  }

  // 4. No active impression: evaluate the trigger.
  return tryTrigger(state, now, config);
}

function tryTrigger(
  state: AutoScheduleNudgeState,
  now: number,
  config: NudgeConfig,
): ReduceResult {
  const cutoff = now - config.triggerWindowMs;
  const recent = state.adjustments.filter((t) => t > cutoff);

  const capReached = state.lifetimeShownCount >= config.lifetimeCap;
  if (recent.length < config.triggerCount || capReached) {
    return { state: { ...state, adjustments: recent }, visible: false, events: [] };
  }

  // Start a new impression. Clear the window so re-triggering needs fresh adjustments.
  return {
    state: {
      ...state,
      adjustments: [],
      lifetimeShownCount: state.lifetimeShownCount + 1,
      activeImpressionStartedAt: now,
    },
    visible: true,
    events: [{ type: 'shown', impressionId: now }],
  };
}

/** Dismiss the active impression and open the cooldown window. */
export function dismiss(
  state: AutoScheduleNudgeState,
  now: number,
  config: NudgeConfig = DEFAULT_NUDGE_CONFIG,
): ReduceResult {
  // Dismiss is only reachable while an impression is showing, but fall back to
  // `now` so the emitted event always carries a usable impression id.
  const impressionId = state.activeImpressionStartedAt ?? now;
  return {
    state: {
      ...state,
      activeImpressionStartedAt: null,
      suppressedUntil: now + config.dismissCooldownMs,
    },
    visible: false,
    events: [{ type: 'dismissed', impressionId }],
  };
}
